// Driver-attribute → personality mapping. The AI controller currently
// consumes a 2-dim profile (`aggression`, `skill`); the on-disk driver
// schema carries 6 attributes (pace / consistency / racecraft /
// tyreManagement / wetWeather / aggression). This module is the seam.
//
// Today: collapse 6 attributes into 2 personality knobs. Future: as
// the controller starts reading more attributes directly (wet weather
// for surface confidence, tyre management for throttle smoothing,
// consistency for input noise), expand the returned shape to expose
// those derived values too — callers that already consume aggression
// + skill keep working unchanged.
//
// Pure / deterministic. Same attributes in → same personality out.

const clamp01 = (v) => Math.max(0, Math.min(1, v));

// Attributes default to 0.5 (mid-pack) when missing so a partially-
// authored driver still behaves reasonably.
function attr(attrs, key) {
  const v = attrs?.[key];
  return Number.isFinite(v) ? v : 0.5;
}

export function driverPersonalityFromAttributes(attrs) {
  // Aggression shapes overtaking decisions and following distance.
  // Most weight on the raw `aggression` attribute; racecraft + pace
  // contribute because a fast, race-savvy driver also commits to
  // moves a slower driver wouldn't.
  const aggression = clamp01(
    attr(attrs, 'aggression') * 0.55 +
    attr(attrs, 'racecraft')  * 0.25 +
    attr(attrs, 'pace')       * 0.20,
  );

  // Skill is a generic "how well does this driver execute" knob — it
  // doesn't drive behavior changes today (the controller floors all
  // cars equally for now), but it's the seam for future input-noise
  // / mistake modeling. Pace contributes most; consistency next; the
  // long-stint fields (tyre, wet, racecraft) round it out.
  const skill = clamp01(
    attr(attrs, 'pace')           * 0.30 +
    attr(attrs, 'consistency')    * 0.25 +
    attr(attrs, 'racecraft')      * 0.20 +
    attr(attrs, 'tyreManagement') * 0.15 +
    attr(attrs, 'wetWeather')     * 0.10,
  );

  return { aggression, skill };
}
